import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Paper,
  Avatar,
  Chip,

  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Map as MapIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  List as ListIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { veterinaireService } from '../../services/api';
import { userService } from '../../services/userService';
import { geolocationService, GeolocationError } from '../../services/geolocationService';
import { VeterinaireAvecDistance, PositionGPS, Veterinaire } from '../../types';
import { CarteVeterinaires } from './CarteVeterinaires';
import L from 'leaflet';

interface MonVeterinaireProps {
  onSuccess?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const MonVeterinaire: React.FC<MonVeterinaireProps> = ({ onSuccess }) => {
  const { user } = useAuth();
  const [veterinairesProches, setVeterinairesProches] = useState<VeterinaireAvecDistance[]>([]);
  const [veterinaireSelectionne, setVeterinaireSelectionne] = useState<Veterinaire | null>(null);
  const [positionUtilisateur, setPositionUtilisateur] = useState<PositionGPS | null>(null);
  const [modeSelection, setModeSelection] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState(0); // 0 = liste, 1 = carte
  const [modeCarteDynamique, setModeCarteDynamique] = useState<boolean>(false); // Mode carte dynamique
  const [rayonRecherche, setRayonRecherche] = useState<number>(10); // Rayon en km
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [dialogVeto, setDialogVeto] = useState<{
    open: boolean;
    veterinaire: VeterinaireAvecDistance | null;
  }>({ open: false, veterinaire: null });

  // Charger le vétérinaire sélectionné et la position de l'utilisateur
  useEffect(() => {
    const chargerDonnees = async () => {
      setLoading(true);
      setError('');

      try {
        // Charger le vétérinaire sélectionné
        if (user?.veterinaire_selectionne) {
          const response = await veterinaireService.getById(user.veterinaire_selectionne);
          setVeterinaireSelectionne(response.data);
        }

        // Obtenir la position de l'utilisateur
        if (user?.latitude && user?.longitude) {
          setPositionUtilisateur({
            latitude: user.latitude,
            longitude: user.longitude
          });
        } else {
          // Essayer d'obtenir la position actuelle
          try {
            const position = await geolocationService.getCurrentPosition();
            setPositionUtilisateur(position);
            
            // Sauvegarder la position de l'utilisateur
            await userService.update(user!.id, {
              latitude: position.latitude,
              longitude: position.longitude
            });
          } catch (geoError) {
            console.warn('Impossible d\'obtenir la position:', geoError);
          }
        }
      } catch (err: any) {
        console.error('Erreur lors du chargement:', err);
        setError('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      chargerDonnees();
    }
  }, [user]);

  // Charger les vétérinaires proches quand on active le mode sélection
  useEffect(() => {
    if (modeSelection && positionUtilisateur) {
      chargerVeterinairesProches();
    }
  }, [modeSelection, positionUtilisateur]); // eslint-disable-line react-hooks/exhaustive-deps

  const chargerVeterinairesProches = async () => {
    if (!positionUtilisateur) return;

    setLoading(true);
    setError('');

    try {
      const response = await veterinaireService.getVeterinairesProches(
        positionUtilisateur.latitude,
        positionUtilisateur.longitude,
        rayonRecherche // Utiliser le rayon sélectionné
      );
      setVeterinairesProches(response.data.results);
    } catch (err: any) {
      console.error('Erreur lors du chargement des vétérinaires proches:', err);
      setError('Erreur lors du chargement des vétérinaires proches');
    } finally {
      setLoading(false);
    }
  };

  // Recharger quand le rayon change
  useEffect(() => {
    if (modeSelection && positionUtilisateur && !modeCarteDynamique) {
      chargerVeterinairesProches();
    }
  }, [rayonRecherche]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nouvelle fonction pour charger les vétérinaires selon les limites de la carte
  const chargerVeterinairesParBounds = async (bounds: L.LatLngBounds) => {
    if (!positionUtilisateur) return;

    try {
      // Calculer le centre des bounds
      const center = bounds.getCenter();
      
      // Calculer la distance maximale depuis le centre jusqu'aux coins
      const northEast = bounds.getNorthEast();
      const southWest = bounds.getSouthWest();
      
      // Utiliser la distance la plus grande pour couvrir toute la zone visible
      const distanceNE = center.distanceTo(northEast) / 1000; // en km
      const distanceSW = center.distanceTo(southWest) / 1000; // en km
      const rayonMax = Math.max(distanceNE, distanceSW, 5); // Au minimum 5km
      
      // Limiter le rayon à 50km pour éviter trop de résultats
      const rayon = Math.min(rayonMax, 50);

      const response = await veterinaireService.getVeterinairesProches(
        center.lat,
        center.lng,
        rayon
      );
      
      // Filtrer pour ne garder que ceux dans les bounds visibles
      const veterinairesVisibles = response.data.results.filter(vet => {
        if (!vet.clinique?.latitude || !vet.clinique?.longitude) return false;
        
        const vetLat = parseFloat(vet.clinique.latitude.toString());
        const vetLng = parseFloat(vet.clinique.longitude.toString());
        
        return bounds.contains([vetLat, vetLng]);
      });
      
      setVeterinairesProches(veterinairesVisibles);
    } catch (err: any) {
      console.error('Erreur lors du chargement des vétérinaires par bounds:', err);
      setError('Erreur lors du chargement des vétérinaires');
    }
  };

  const obtenirPosition = async () => {
    setLoading(true);
    setError('');

    try {
      const position = await geolocationService.getCurrentPosition();
      setPositionUtilisateur(position);
      
      // Sauvegarder la position
      if (user) {
        await userService.update(user.id, {
          latitude: position.latitude,
          longitude: position.longitude
        });
      }
    } catch (err: any) {
      const geoErr = err as GeolocationError;
      setError(geoErr.message);
    } finally {
      setLoading(false);
    }
  };

  const afficherDetailVeterinaire = (veterinaire: VeterinaireAvecDistance) => {
    setDialogVeto({ open: true, veterinaire });
  };

  const selectionnerVeterinaire = async (veterinaire: VeterinaireAvecDistance) => {
    if (!user) return;

    setLoading(true);
    try {
      // Créer un objet User pour le vétérinaire si nécessaire
      const veterinaireUser = await veterinaireService.getById(veterinaire.id);
      
      // Mettre à jour le propriétaire avec le vétérinaire sélectionné
      await userService.update(user.id, {
        veterinaire_selectionne: veterinaire.id
      });

      setVeterinaireSelectionne(veterinaireUser.data);
      setModeSelection(false);
      setDialogVeto({ open: false, veterinaire: null });
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Erreur lors de la sélection:', err);
      setError('Erreur lors de la sélection du vétérinaire');
    } finally {
      setLoading(false);
    }
  };

  const retirerVeterinaire = async () => {
    if (!user) return;

    setLoading(true);
    try {
      await userService.update(user.id, {
        veterinaire_selectionne: null
      });

      setVeterinaireSelectionne(null);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Erreur lors du retrait:', err);
      setError('Erreur lors du retrait du vétérinaire');
    } finally {
      setLoading(false);
    }
  };

  const renderListeVeterinaires = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      {veterinairesProches.map((veterinaire) => (
        <Card key={veterinaire.id} sx={{ cursor: 'pointer' }} onClick={() => afficherDetailVeterinaire(veterinaire)}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <PersonIcon />
              </Avatar>
              <Box flex={1}>
                <Typography variant="h6">
                  Dr. {veterinaire.prenom} {veterinaire.nom}
                </Typography>
                {veterinaire.specialite && (
                  <Chip label={veterinaire.specialite} size="small" sx={{ mt: 0.5, mb: 1 }} />
                )}
                {veterinaire.clinique && (
                  <Typography variant="body2" color="text.secondary">
                    {veterinaire.clinique.nom} - {veterinaire.clinique.ville}
                  </Typography>
                )}
                {veterinaire.distance && (
                  <Typography variant="body2" color="primary">
                    À {veterinaire.distance.toFixed(1)} km
                  </Typography>
                )}
              </Box>
              <Button
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  afficherDetailVeterinaire(veterinaire);
                }}
              >
                Voir détails
              </Button>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  if (loading && !veterinairesProches.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Mon vétérinaire
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={modeSelection}
                onChange={(e) => setModeSelection(e.target.checked)}
                disabled={!positionUtilisateur}
              />
            }
            label="Mode sélection"
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={obtenirPosition}
            disabled={loading}
          >
            Actualiser position
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!positionUtilisateur && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Pour utiliser cette fonctionnalité, nous avons besoin de votre position géographique.
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={obtenirPosition}
            sx={{ mt: 1 }}
            disabled={loading}
          >
            Autoriser la géolocalisation
          </Button>
        </Alert>
      )}

      {/* Mode normal : affichage du vétérinaire sélectionné */}
      {!modeSelection && (
        <Box>
          {veterinaireSelectionne ? (
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={3}>
                  <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}>
                    <PersonIcon sx={{ fontSize: 40 }} />
                  </Avatar>
                  
                  <Box flex={1}>
                    <Typography variant="h5" gutterBottom>
                      Dr. {veterinaireSelectionne.prenom} {veterinaireSelectionne.nom}
                    </Typography>
                    
                    {veterinaireSelectionne.specialite && (
                      <Chip 
                        label={veterinaireSelectionne.specialite} 
                        color="primary" 
                        size="small" 
                        sx={{ mb: 2 }}
                      />
                    )}

                    <Box display="flex" flexDirection="column" gap={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <EmailIcon color="action" />
                        <Typography variant="body2">
                          {veterinaireSelectionne.email}
                        </Typography>
                      </Box>
                      
                      <Box display="flex" alignItems="center" gap={1}>
                        <PhoneIcon color="action" />
                        <Typography variant="body2">
                          {veterinaireSelectionne.telephone}
                        </Typography>
                      </Box>

                      {veterinaireSelectionne.clinique && (
                        <Box display="flex" alignItems="start" gap={1}>
                          <LocationIcon color="action" sx={{ mt: 0.5 }} />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {veterinaireSelectionne.clinique.nom}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {veterinaireSelectionne.clinique.adresse}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {veterinaireSelectionne.clinique.code_postal} {veterinaireSelectionne.clinique.ville}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  <Box>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={retirerVeterinaire}
                      disabled={loading}
                    >
                      Retirer
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <PersonIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Aucun vétérinaire sélectionné
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Activez le mode de sélection pour choisir un vétérinaire proche de chez vous.
              </Typography>
              <Button
                variant="contained"
                startIcon={<MapIcon />}
                onClick={() => setModeSelection(true)}
                disabled={!positionUtilisateur}
              >
                Choisir un vétérinaire
              </Button>
            </Paper>
          )}
        </Box>
      )}

      {/* Mode sélection : onglets liste/carte */}
      {modeSelection && positionUtilisateur && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
            <Typography variant="h6">
              Vétérinaires disponibles
            </Typography>
            
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
              {/* Sélecteur de rayon */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Rayon</InputLabel>
                <Select
                  value={rayonRecherche}
                  label="Rayon"
                  onChange={(e) => setRayonRecherche(e.target.value as number)}
                  disabled={modeCarteDynamique}
                >
                  <MenuItem value={10}>10 km</MenuItem>
                  <MenuItem value={30}>30 km</MenuItem>
                  <MenuItem value={50}>50 km</MenuItem>
                  <MenuItem value={100}>100 km</MenuItem>
                </Select>
              </FormControl>

              {/* Option pour le mode carte dynamique */}
              {tabValue === 1 && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={modeCarteDynamique}
                      onChange={(e) => setModeCarteDynamique(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Recherche dynamique"
                />
              )}
            </Box>
          </Box>
          
          {loading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          
          {!loading && (
            <Paper sx={{ width: '100%' }}>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                <Tab 
                  icon={<ListIcon />} 
                  label="Liste" 
                  id="tab-0"
                />
                <Tab 
                  icon={<MapIcon />} 
                  label="Carte" 
                  id="tab-1"
                />
              </Tabs>
              
              <TabPanel value={tabValue} index={0}>
                {veterinairesProches.length > 0 ? (
                  <>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {veterinairesProches.length} vétérinaire{veterinairesProches.length > 1 ? 's' : ''} 
                      dans un rayon de {rayonRecherche} km
                    </Typography>
                    {renderListeVeterinaires()}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                    Aucun vétérinaire trouvé dans un rayon de {rayonRecherche} km. 
                    Essayez d'augmenter le rayon de recherche.
                  </Typography>
                )}
              </TabPanel>
              
              <TabPanel value={tabValue} index={1}>
                <CarteVeterinaires
                  veterinaires={veterinairesProches}
                  positionUtilisateur={positionUtilisateur}
                  onVeterinaireClick={afficherDetailVeterinaire}
                  rayon={modeCarteDynamique ? undefined : rayonRecherche}
                  loading={loading}
                  onLoadVeterinaires={modeCarteDynamique ? chargerVeterinairesParBounds : undefined}
                />
              </TabPanel>
            </Paper>
          )}
        </Box>
      )}

      {/* Dialog de détail du vétérinaire */}
      <Dialog
        open={dialogVeto.open}
        onClose={() => setDialogVeto({ open: false, veterinaire: null })}
        maxWidth="md"
        fullWidth
      >
        {dialogVeto.veterinaire && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    Dr. {dialogVeto.veterinaire.prenom} {dialogVeto.veterinaire.nom}
                  </Typography>
                  {dialogVeto.veterinaire.distance && (
                    <Typography variant="body2" color="text.secondary">
                      À {dialogVeto.veterinaire.distance.toFixed(1)} km de chez vous
                    </Typography>
                  )}
                </Box>
              </Box>
            </DialogTitle>
            
            <DialogContent>
              <Box display="flex" flexDirection="column" gap={2}>
                {dialogVeto.veterinaire.specialite && (
                  <Chip 
                    label={dialogVeto.veterinaire.specialite} 
                    color="primary" 
                    size="small" 
                  />
                )}

                <Box display="flex" alignItems="center" gap={1}>
                  <EmailIcon color="action" />
                  <Typography variant="body1">
                    {dialogVeto.veterinaire.email}
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon color="action" />
                  <Typography variant="body1">
                    {dialogVeto.veterinaire.telephone}
                  </Typography>
                </Box>

                {dialogVeto.veterinaire.clinique && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Clinique
                    </Typography>
                    <Box display="flex" alignItems="start" gap={1}>
                      <LocationIcon color="action" sx={{ mt: 0.5 }} />
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {dialogVeto.veterinaire.clinique.nom}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {dialogVeto.veterinaire.clinique.adresse}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {dialogVeto.veterinaire.clinique.code_postal} {dialogVeto.veterinaire.clinique.ville}
                        </Typography>
                        {dialogVeto.veterinaire.clinique.telephone && (
                          <Typography variant="body2" color="text.secondary">
                            Tél: {dialogVeto.veterinaire.clinique.telephone}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </DialogContent>
            
            <DialogActions>
              <Button
                onClick={() => setDialogVeto({ open: false, veterinaire: null })}
                startIcon={<CloseIcon />}
              >
                Fermer
              </Button>
              <Button
                variant="contained"
                onClick={() => selectionnerVeterinaire(dialogVeto.veterinaire!)}
                startIcon={<CheckIcon />}
                disabled={loading}
              >
                Sélectionner ce vétérinaire
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}; 